import { Expense, Floor, Invoice, Room, User } from '../../types';
import {
  computeUnpaidRentMonthsBeforeCurrent,
  getCurrentMonthRentStatus,
  getRentPeriodBalanceDue,
  groupInvoicesByUserId,
  isRentMonthBillable,
  isRentPeriodFullyPaid,
  tenantRentAmount,
} from '../../utils/tenantPaymentStatus';
import { normalizePaymentType } from '../../utils/paymentDisplay';
import { formatTenantListLocation } from '../../utils/locationLabels';
import { filterResidentTenants } from '../../utils/residentTenants';
import { isActiveTenant } from '../../utils/tenantStatus';

export function num(v: unknown): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

export function formatINR(amount: number): string {
  return `₹ ${amount.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
}

export function formatINRCompact(amount: number): string {
  if (amount >= 100000) return `₹ ${(amount / 100000).toFixed(2)}L`;
  if (amount >= 1000) return `₹ ${(amount / 1000).toFixed(1)}K`;
  return formatINR(amount);
}

export function tenantDisplayName(t: User): string {
  return (t.username || t.phoneNumber || `Tenant #${t.id}`).trim();
}

export type FloorRoomLookup = {
  floorById?: Record<number, Floor>;
  roomById?: Record<number, Room>;
};

export function buildFloorRoomLookup(floors: Floor[], rooms: Room[]): FloorRoomLookup {
  return {
    floorById: Object.fromEntries(floors.map((f) => [f.id, f])) as Record<number, Floor>,
    roomById: Object.fromEntries(rooms.map((r) => [r.id, r])) as Record<number, Room>,
  };
}

/** Compact floor + room label (e.g. "1-101"). */
export function tenantLocation(t: User, lookup?: FloorRoomLookup): string {
  return formatTenantListLocation(t, lookup ?? {});
}

export type TenantFinancialRow = {
  tenant: User;
  rent: number;
  currentMonthLabel: string;
  currentMonthPaid: boolean;
  currentMonthDue: number;
  previousUnpaid: number;
  totalOutstanding: number;
  expectedCollection: number;
  collectedLifetime: number;
  collectedInPeriod: number;
  remainingAmount: number;
  advancePaid: number;
  advanceBalance: number;
  unpaidMonthCount: number;
};

function emptyFinancialRow(
  tenant: User,
  invoices: Invoice[],
  periodStart?: string,
  periodEnd?: string,
): TenantFinancialRow {
  const activeInvs = invoices.filter((i) => !i.isDeleted);
  let collectedLifetime = 0;
  let collectedInPeriod = 0;
  for (const inv of activeInvs) {
    const paid = num(inv.paidAmount);
    if (normalizePaymentType(inv.paymentType) === 'REFUND') continue;
    collectedLifetime += paid;
    if (periodStart && periodEnd && inv.paymentTimestamp) {
      const ts = new Date(inv.paymentTimestamp).getTime();
      const start = new Date(periodStart);
      start.setHours(0, 0, 0, 0);
      const end = new Date(periodEnd);
      end.setHours(23, 59, 59, 999);
      if (ts >= start.getTime() && ts <= end.getTime()) {
        collectedInPeriod += paid;
      }
    }
  }
  return {
    tenant,
    rent: tenantRentAmount(tenant),
    currentMonthLabel: '—',
    currentMonthPaid: true,
    currentMonthDue: 0,
    previousUnpaid: 0,
    totalOutstanding: 0,
    expectedCollection: 0,
    collectedLifetime,
    collectedInPeriod,
    remainingAmount: 0,
    advancePaid: 0,
    advanceBalance: 0,
    unpaidMonthCount: 0,
  };
}

export function buildTenantFinancialRow(
  tenant: User,
  invoices: Invoice[],
  periodStart?: string,
  periodEnd?: string,
): TenantFinancialRow {
  const activeInvs = invoices.filter((i) => !i.isDeleted);
  if (!isActiveTenant(tenant)) {
    return emptyFinancialRow(tenant, invoices, periodStart, periodEnd);
  }

  const rent = tenantRentAmount(tenant);

  const current = getCurrentMonthRentStatus(tenant, activeInvs);
  const unpaidMonths = computeUnpaidRentMonthsBeforeCurrent(tenant, activeInvs);
  const previousUnpaid = unpaidMonths.reduce((s, r) => s + r.balanceDue, 0);
  const currentMonthDue = current?.balanceDue ?? 0;
  const currentMonthPaid = current?.isFullyPaid ?? false;

  let advancePaid = 0;
  let advanceBalance = 0;
  for (const inv of activeInvs) {
    if (normalizePaymentType(inv.paymentType) !== 'ADVANCE') continue;
    advancePaid += num(inv.paidAmount);
    advanceBalance += num(inv.balanceAmount);
  }

  const totalOutstanding = previousUnpaid + currentMonthDue + advanceBalance;
  const expectedCollection = totalOutstanding;

  let collectedLifetime = 0;
  let collectedInPeriod = 0;
  for (const inv of activeInvs) {
    const paid = num(inv.paidAmount);
    if (normalizePaymentType(inv.paymentType) === 'REFUND') continue;
    collectedLifetime += paid;
    if (periodStart && periodEnd && inv.paymentTimestamp) {
      const ts = new Date(inv.paymentTimestamp).getTime();
      const start = new Date(periodStart);
      start.setHours(0, 0, 0, 0);
      const end = new Date(periodEnd);
      end.setHours(23, 59, 59, 999);
      if (ts >= start.getTime() && ts <= end.getTime()) {
        collectedInPeriod += paid;
      }
    }
  }

  return {
    tenant,
    rent,
    currentMonthLabel: current?.periodLabel ?? '—',
    currentMonthPaid,
    currentMonthDue,
    previousUnpaid,
    totalOutstanding,
    expectedCollection,
    collectedLifetime,
    collectedInPeriod,
    remainingAmount: totalOutstanding,
    advancePaid,
    advanceBalance,
    unpaidMonthCount: unpaidMonths.length + (currentMonthDue > 0 ? 1 : 0),
  };
}

export function isPaidInMonth(tenant: User, invoices: Invoice[], month: string, year: string): boolean {
  return isRentPeriodFullyPaid(tenant, invoices.filter((i) => !i.isDeleted), month, year);
}

export function isUnpaidInMonth(tenant: User, invoices: Invoice[], month: string, year: string): boolean {
  if (tenantRentAmount(tenant) <= 0) return false;
  const active = invoices.filter((i) => !i.isDeleted);
  if (!isRentMonthBillable(tenant, month, year, active)) return false;
  return getRentPeriodBalanceDue(tenant, active, month, year) > 0.01;
}

export type PendingPaymentRow = {
  tenant: User;
  amount: number;
  currentMonthDue: number;
  previousUnpaid: number;
  advanceBalance: number;
};

export type PendingPaymentsSummary = {
  totalAmount: number;
  tenantCount: number;
  top: PendingPaymentRow[];
};

export type MonthlyExpectedCollection = {
  amount: number;
  activeTenantCount: number;
  tenantsWithRent: number;
};

/** Sum of monthly rent for currently staying (active) resident tenants. */
export function computeMonthlyExpectedCollection(tenants: User[]): MonthlyExpectedCollection {
  const staying = filterResidentTenants(tenants).filter((t) => !t.isDeleted && t.isActive !== false);
  let amount = 0;
  let tenantsWithRent = 0;
  for (const t of staying) {
    const rent = tenantRentAmount(t);
    if (rent > 0) {
      amount += rent;
      tenantsWithRent += 1;
    }
  }
  return { amount, activeTenantCount: staying.length, tenantsWithRent };
}

/** Outstanding rent + advance balances for active tenants only. */
export function computePendingPaymentsSummary(
  tenants: User[],
  invoicesByUser: Map<number, Invoice[]>,
  topN = 8,
): PendingPaymentsSummary {
  const top: PendingPaymentRow[] = [];
  let totalAmount = 0;
  let tenantCount = 0;

  const activeTenants = filterResidentTenants(tenants).filter(isActiveTenant);

  for (const tenant of activeTenants) {
    const invs = invoicesByUser.get(tenant.id) || [];
    const fin = buildTenantFinancialRow(tenant, invs);
    if (fin.remainingAmount <= 0.01) continue;
    tenantCount += 1;
    totalAmount += fin.remainingAmount;
    top.push({
      tenant,
      amount: fin.remainingAmount,
      currentMonthDue: fin.currentMonthDue,
      previousUnpaid: fin.previousUnpaid,
      advanceBalance: fin.advanceBalance,
    });
  }

  top.sort((a, b) => b.amount - a.amount);
  return { totalAmount, tenantCount, top: top.slice(0, topN) };
}

export { groupInvoicesByUserId };

export function sumExpensesByCategory(expenses: Expense[]): { category: string; total: number }[] {
  const map = new Map<string, number>();
  for (const e of expenses) {
    const cat = e.category || 'OTHERS';
    map.set(cat, (map.get(cat) || 0) + num(e.amount));
  }
  return Array.from(map.entries())
    .map(([category, total]) => ({ category, total }))
    .sort((a, b) => b.total - a.total);
}

export const EXPENSE_CATEGORY_LABELS: Record<string, string> = {
  STAFF_SALARY: 'Salaries',
  UTILITIES: 'Electricity',
  MAINTENANCE: 'Maintenance',
  SUPPLIES: 'Supplies',
  FOOD: 'Food / Mess',
  TRANSPORT: 'Transport',
  OTHERS: 'Others',
};
